import React from 'react';
import { get, merge, size } from 'lodash';

// React view component to render
import { memberHealthConditions as MemberHealthConditionsView } from 'ui/views/widgets/memberHealthConditions';
import { MemberHealthConditionsService } from 'services/memberHealthConditions';
import { SERVICE_RESPONSE_STATUS, GLOBAL_LABELS } from 'constants/common';
import { OVERLAY_VIEWS } from 'constants/templates';
import { overlay } from 'modules/overlay';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';
import {  SIZES } from 'constants/common';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberHealthConditions } from 'ui/containers/MemberHealthConditions';
 *
 * // render
 * <MemberHealthConditions propName='propValue'/>
 */
export class MemberHealthConditions extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            selectedHealthConditions: undefined,
            note: undefined,
            networkError: undefined,
            isNoKnownHealthConditions:false ,
            isHealthConditionsNotAtVerified: false
        };

        // Member Health Conditions Info Service
        this.memberHealthConditionsService = new MemberHealthConditionsService();

        // bind methods to instance
        this.editClickHandler = this.editClickHandler.bind( this );

        // Member Health Conditions Info data fetch
        this.fetchComponentData();
    }

    /**************************************************** */

    /**
     * @desc Fetch Health Conditions Info.
     */
    async fetchComponentData() {
       
        log.debug( 'MemberHealthConditions.fetchComponentData()' );

        // cancel ongoing request
        this.memberHealthConditionsService.cancel();

        try {
            const { status, data }  = await this.memberHealthConditionsService.fetchData( {
                url: this.props.widget.url,
                data: this.props.requestPayload,
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                    this.setState( {
                        isFetching: false,
                        componentData: data,
                        networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
                        networkError: data
                    } );
                    
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    // open add edit form as well as call Add or Edit functionality depends upon the action
    async editClickHandler( { action } ) {
        log.debug( 'MemberHealthConditions.editClickHandler()' );
        // cancel ongoing request
        this.memberHealthConditionsService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberHealthConditionsService.fetchAddEditForm( {
            url: action.url,
            data: this.props.requestPayload
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.ADD_EDIT_HEALTH_CONDITIONS,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    onCancelClick: () => {
                        overlay.close();
                    },
                    onUpdate: ( { selectedItems, note, isHealthConditionsNotAtVerified, isNoKnownHealthConditions } ) => {
                        this.setState( {
                            selectedHealthConditions: selectedItems,
                            note: note,
                            isHealthConditionsNotAtVerified: isHealthConditionsNotAtVerified,
                            isNoKnownHealthConditions: isNoKnownHealthConditions
                        } );
                    },
                    onSumbitClick: async () => {
                        overlay.close();
                        // show page level preloader
                        pageLevelPreloader.show();
                        console.log('this.state::',this.state);
                        if( !this.state.isHealthConditionsNotAtVerified  && !this.state.isNoKnownHealthConditions && this.state.selectedHealthConditions.length && 'none'==this.state.selectedHealthConditions[0] )  
                        {
                           pageLevelPreloader.hide();
                           errorOverlay.open( { message: 'Please select any one of above option ' , size: SIZES.SMALL } ) 
                        }else{
                        // get status and service data
                        const { _status } = await this.memberHealthConditionsService.saveEditHealthConditions( {
                            url: action.config.submitUrl,
                            data: merge( {
                                data: {
                                    ids: this.state.selectedHealthConditions,
                                    note: this.state.note,
                                    isHealthConditionsNotAtVerified: this.state.isHealthConditionsNotAtVerified,
                                    isNoKnownHealthConditions: this.state.isNoKnownHealthConditions
                                } }, this.props.requestPayload )
                        } );

                        // show page level preloader
                        pageLevelPreloader.hide();

                        // check if response status was success
                        if ( SERVICE_RESPONSE_STATUS.SUCCESS === _status ) {

                            // display success message
                            successOverlay.open( {
                                message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
                            } );

                            // Member Health Condition Info data fetch
                            this.setState( {
                            isFetching: true,
                            networkStatus: undefined,
                        }, () =>  this.fetchComponentData() );

                        } else {
                            errorOverlay.open(); // show error
                        }
                    }
                    },
                }
            } );
        }
    }

    /**
     * @desc When component unmounts, cleanup resources.
     */
    componentWillUnmount() {
        this.memberHealthConditionsService.cancel(); // cancel ongoing requests
    }

    /****************************************************** */

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberHealthConditionsView
                isFetching= { this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                requestPayload={ this.props.requestPayload }
                { ...get( this.state, 'componentData', {} ) }
                onClickEdit={ this.editClickHandler }
                networkError={ this.state.networkError }
            />
        );
    }
}

// set display name
MemberHealthConditions.displayName = 'MemberHealthConditions';

// set default props
MemberHealthConditions.defaultProps = {};

// prop types
MemberHealthConditions.propTypes = {};
